import {
  AnimationConfigParams,
  KeyframeConfig,
  ZOOM_ANIMATIONS,
  TUMBLE_ANIMATIONS,
  FADE_ANIMATIONS,
  SHIFT_ANIMATIONS,
  FLY_ANIMATIONS,
  FLICKER_ANIMATIONS,
  PULSE_ANIMATIONS,
  WIPE_ANIMATIONS,
  REVEAL_ANIMATIONS,
  BLOCK_REVEAL_ANIMATIONS,
  AnimationConfigExtraParams,
  CUSTOM_ANIMATIONS,
  ANIMATIONS,
} from "../../models/animations.model";
import { ObjectMeasures } from "../../models/canvas.model";
import { getBlockRevealBaseKeyframe } from "./keyframes/blockReveal";
import { getBreatheBaseKeyframe } from "./keyframes/breathe";
import {
  getFadeInBaseKeyframe,
  getFadeOutBaseKeyframe,
} from "./keyframes/fade";
import { getFlickerBaseKeyframe } from "./keyframes/flicker";
import {
  getFlyInTopBaseKeyframe,
  getFlyInBottomBaseKeyframe,
  getFlyInLeftBaseKeyframe,
  getFlyInRightBaseKeyframe,
  getFlyOutTopBaseKeyframe,
  getFlyOutBottomBaseKeyframe,
  getFlyOutLeftBaseKeyframe,
  getFlyOutRightBaseKeyframe,
} from "./keyframes/fly";
import { getGreatThinkerBaseKeyframe } from "./keyframes/greatThinker";
import { getMadeWithLoveBaseKeyframe } from "./keyframes/madeWithLove";
import { getPulseBaseKeyframe } from "./keyframes/pulse";
import {
  getRevealInLeftBaseKeyframe,
  getRevealInRightBaseKeyframe,
  getRevealInTopBaseKeyframe,
  getRevealInBottomBaseKeyframe,
  getRevealOutLeftBaseKeyframe,
  getRevealOutRightBaseKeyframe,
  getRevealOutTopBaseKeyframe,
  getRevealOutBottomBaseKeyframe,
  getBeautifulQuestionsBaseKeyframe,
} from "./keyframes/reveal";
import {
  getRisingStrongInBaseKeyframe,
  getRisingStrongOutBaseKeyframe,
} from "./keyframes/risingStrong";
import { getDominoDreamsBaseKeyframe } from "./keyframes/dominoDreams";
import {
  getShiftTopBaseKeyframe,
  getShiftBottomBaseKeyframe,
  getShiftLeftBaseKeyframe,
  getShiftRightBaseKeyframe,
} from "./keyframes/shift";
import {
  getHideWithOpacityKeyframe,
  getShowGroupObjectsBaseKeyframe,
  getShowKeyframe,
} from "./keyframes/showHide";
import { getStompBaseKeyframe } from "./keyframes/stomp";
import { getSunnyMorningsBaseKeyframe } from "./keyframes/sunnyMornings";
import {
  getTumbleInBaseKeyframe,
  getTumbleOutBaseKeyframe,
} from "./keyframes/tumble";
import {
  getWipeInLeftBaseKeyframe,
  getWipeInRightBaseKeyframe,
  getWipeInTopBaseKeyframe,
  getWipeInBottomBaseKeyframe,
  getWipeOutLeftBaseKeyframe,
  getWipeOutRightBaseKeyframe,
  getWipeOutTopBaseKeyframe,
  getWipeOutBottomBaseKeyframe,
} from "./keyframes/wipe";
import {
  getZoomInBaseKeyframe,
  getZoomOutBaseKeyframe,
} from "./keyframes/zoom";
import {
  getANewProductionInBaseKeyframe,
  getANewProductionOutBaseKeyframe,
} from "./keyframes/aNewProduction";
import { getCoffeeMorningsBaseKeyframe } from "./keyframes/coffeeMornings";
import { getFindYourElementBaseKeyframe } from "./keyframes/findYourElement";
import { getHelloGoodbyeBaseKeyframe } from "./keyframes/helloGoodbye";
import { getThursdayBaseKeyframe } from "./keyframes/thursday";

export type Keyframe = {
  left?: number;
  top?: number;
  right?: number;
  bottom?: number;
  opacity?: number;
  duration?: number;
  scaleX?: number;
  scaleY?: number;
  angle?: number;
  width?: number;
  height?: number;
  translateX?: number;
  translateY?: number;
  originX?: string;
  originY?: string;
  visible?: boolean;
};

const setDefaultOpacityToKeyframes = (
  keyframes: Keyframe[],
  opacity: number
): void => {
  keyframes.map((keyframe) => {
    keyframe.opacity = keyframe.opacity ?? opacity;
    return keyframe;
  });
};

const setDurationToKeyframes = (
  keyframes: Keyframe[],
  duration: number
): void => {
  /**
   * Supposed to work only for keyframes with 2 keyframes in them.
   *
   * Duration starts at 1 millisecond because
   * as of now animejs immediately sets the keyframe property to the object
   * if the duration is 0, regardless of offset
   */
  if (keyframes[0]) {
    keyframes[0].duration = keyframes[0].duration ?? 1;
  }

  if (keyframes[1]) {
    keyframes[1].duration = keyframes[1].duration ?? duration;
  }
};

export const getKeyframeConfigs = (
  params: AnimationConfigParams
): KeyframeConfig[] => {
  const { fabricObject, fabricObjectClone, effect, workarea, extras } = params;

  const originalObjectMeasures = {
    top: fabricObjectClone.top,
    left: fabricObjectClone.left,
    width: fabricObjectClone.width,
    height: fabricObjectClone.height,
  };

  const baseKeyframes = getBaseKeyframes(
    effect.type,
    workarea,
    originalObjectMeasures,
    fabricObject,
    fabricObjectClone,
    effect.trans_duration,
    extras
  );

  const keyframeConfigs: KeyframeConfig[] = [];

  for (const baseKeyframe of baseKeyframes) {
    setDurationToKeyframes(
      baseKeyframe.option.keyframes,
      effect.trans_duration
    );

    /**
     * Temporarily required to keep them visible
     */
    setDefaultOpacityToKeyframes(
      baseKeyframe.option.keyframes,
      fabricObjectClone.opacity
    );

    keyframeConfigs.push(baseKeyframe);
  }

  return keyframeConfigs;
};

export const getBaseKeyframes = (
  type: ANIMATIONS,
  canvasMeasures: ObjectMeasures,
  objectMeasures: ObjectMeasures,
  fabricObject: any,
  fabricObjectClone: any,
  duration: number,
  extras: AnimationConfigExtraParams
): KeyframeConfig[] => {
  switch (type) {
    case FADE_ANIMATIONS.FADE_IN:
    default:
      return getFadeInBaseKeyframe(fabricObjectClone.opacity);
    case FADE_ANIMATIONS.FADE_OUT:
      return getFadeOutBaseKeyframe(fabricObjectClone.opacity);
    case FLY_ANIMATIONS.FLY_IN_TOP:
      return getFlyInTopBaseKeyframe(objectMeasures, canvasMeasures);
    case FLY_ANIMATIONS.FLY_IN_BOTTOM:
      return getFlyInBottomBaseKeyframe(objectMeasures, canvasMeasures);
    case FLY_ANIMATIONS.FLY_IN_LEFT:
      return getFlyInLeftBaseKeyframe(objectMeasures, canvasMeasures);
    case FLY_ANIMATIONS.FLY_IN_RIGHT:
      return getFlyInRightBaseKeyframe(objectMeasures, canvasMeasures);
    case FLY_ANIMATIONS.FLY_OUT_TOP:
      return getFlyOutTopBaseKeyframe(objectMeasures, canvasMeasures);
    case FLY_ANIMATIONS.FLY_OUT_BOTTOM:
      return getFlyOutBottomBaseKeyframe(objectMeasures, canvasMeasures);
    case FLY_ANIMATIONS.FLY_OUT_LEFT:
      return getFlyOutLeftBaseKeyframe(objectMeasures, canvasMeasures);
    case FLY_ANIMATIONS.FLY_OUT_RIGHT:
      return getFlyOutRightBaseKeyframe(objectMeasures, canvasMeasures);
    case ZOOM_ANIMATIONS.ZOOM_IN:
      return getZoomInBaseKeyframe(
        fabricObjectClone.scaleX,
        fabricObjectClone.scaleY
      );
    case ZOOM_ANIMATIONS.ZOOM_OUT:
      return getZoomOutBaseKeyframe(
        fabricObjectClone.scaleX,
        fabricObjectClone.scaleY
      );
    case SHIFT_ANIMATIONS.SHIFT_TOP:
      return getShiftTopBaseKeyframe(
        objectMeasures,
        canvasMeasures,
        fabricObjectClone.opacity
      );
    case SHIFT_ANIMATIONS.SHIFT_BOTTOM:
      return getShiftBottomBaseKeyframe(
        objectMeasures,
        canvasMeasures,
        fabricObjectClone.opacity
      );
    case SHIFT_ANIMATIONS.SHIFT_LEFT:
      return getShiftLeftBaseKeyframe(
        objectMeasures,
        canvasMeasures,
        fabricObjectClone.opacity
      );
    case SHIFT_ANIMATIONS.SHIFT_RIGHT:
      return getShiftRightBaseKeyframe(
        objectMeasures,
        canvasMeasures,
        fabricObjectClone.opacity
      );
    case TUMBLE_ANIMATIONS.TUMBLE_IN:
      return getTumbleInBaseKeyframe(
        objectMeasures,
        canvasMeasures,
        fabricObjectClone.angle
      );
    case TUMBLE_ANIMATIONS.TUMBLE_OUT:
      return getTumbleOutBaseKeyframe(
        objectMeasures,
        canvasMeasures,
        fabricObjectClone.angle
      );
    case FLICKER_ANIMATIONS.FLICKER:
      return getFlickerBaseKeyframe(fabricObjectClone.opacity, duration);
    case PULSE_ANIMATIONS.PULSE:
      return getPulseBaseKeyframe(fabricObjectClone.opacity, duration);
    case ZOOM_ANIMATIONS.STOMP:
      return getStompBaseKeyframe(
        fabricObjectClone.scaleX,
        fabricObjectClone.scaleY
      );
    case ZOOM_ANIMATIONS.BREATHE:
      return getBreatheBaseKeyframe(
        fabricObjectClone.scaleX,
        fabricObjectClone.scaleY
      );
    case WIPE_ANIMATIONS.WIPE_IN_LEFT:
      return getWipeInLeftBaseKeyframe(
        fabricObjectClone.clipPath.left,
        fabricObjectClone.width,
        fabricObject
      );
    case WIPE_ANIMATIONS.WIPE_IN_RIGHT:
      return getWipeInRightBaseKeyframe(
        fabricObjectClone.clipPath.left,
        fabricObjectClone.width,
        fabricObject
      );
    case WIPE_ANIMATIONS.WIPE_IN_TOP:
      return getWipeInTopBaseKeyframe(
        fabricObjectClone.clipPath.top,
        fabricObjectClone.height,
        fabricObject,
        duration
      );
    case WIPE_ANIMATIONS.WIPE_IN_BOTTOM:
      return getWipeInBottomBaseKeyframe(
        fabricObjectClone.clipPath.top,
        fabricObjectClone.height,
        fabricObject
      );
    case WIPE_ANIMATIONS.WIPE_OUT_LEFT:
      return getWipeOutLeftBaseKeyframe(
        fabricObjectClone.clipPath.left,
        fabricObjectClone.width,
        fabricObject
      );
    case WIPE_ANIMATIONS.WIPE_OUT_RIGHT:
      return getWipeOutRightBaseKeyframe(
        fabricObjectClone.clipPath.left,
        fabricObjectClone.width,
        fabricObject
      );
    case WIPE_ANIMATIONS.WIPE_OUT_TOP:
      return getWipeOutTopBaseKeyframe(
        fabricObjectClone.clipPath.top,
        fabricObjectClone.height,
        fabricObject
      );
    case WIPE_ANIMATIONS.WIPE_OUT_BOTTOM:
      return getWipeOutBottomBaseKeyframe(
        fabricObjectClone.clipPath.top,
        fabricObjectClone.height,
        fabricObject
      );
    case REVEAL_ANIMATIONS.REVEAL_IN_LEFT:
      return getRevealInLeftBaseKeyframe(
        fabricObjectClone.clipPath.left,
        fabricObjectClone.width,
        fabricObjectClone.left,
        fabricObjectClone.opacity,
        fabricObject,
        fabricObjectClone.scaleX
      );
    case REVEAL_ANIMATIONS.REVEAL_IN_RIGHT:
      return getRevealInRightBaseKeyframe(
        fabricObjectClone.width,
        fabricObjectClone.left,
        fabricObjectClone.opacity,
        fabricObject,
        fabricObjectClone.scaleX
      );
    case REVEAL_ANIMATIONS.REVEAL_IN_TOP:
      return getRevealInTopBaseKeyframe(
        fabricObjectClone.clipPath.top,
        fabricObjectClone.height,
        fabricObjectClone.top,
        fabricObjectClone.opacity,
        fabricObject,
        fabricObjectClone.scaleY
      );
    case REVEAL_ANIMATIONS.REVEAL_IN_BOTTOM:
      return getRevealInBottomBaseKeyframe(
        fabricObjectClone.height,
        fabricObjectClone.top,
        fabricObjectClone.opacity,
        fabricObject,
        fabricObjectClone.scaleY
      );
    case REVEAL_ANIMATIONS.REVEAL_OUT_LEFT:
      return getRevealOutLeftBaseKeyframe(
        fabricObjectClone.clipPath.left,
        fabricObjectClone.width,
        fabricObjectClone.left,
        fabricObjectClone.opacity,
        fabricObject,
        fabricObjectClone.scaleX
      );
    case REVEAL_ANIMATIONS.REVEAL_OUT_RIGHT:
      return getRevealOutRightBaseKeyframe(
        fabricObjectClone.width,
        fabricObjectClone.left,
        fabricObjectClone.opacity,
        fabricObject,
        fabricObjectClone.scaleX
      );
    case REVEAL_ANIMATIONS.REVEAL_OUT_TOP:
      return getRevealOutTopBaseKeyframe(
        fabricObjectClone.clipPath.top,
        fabricObjectClone.height,
        fabricObjectClone.top,
        fabricObjectClone.opacity,
        fabricObject,
        fabricObjectClone.scaleY
      );
    case REVEAL_ANIMATIONS.REVEAL_OUT_BOTTOM:
      return getRevealOutBottomBaseKeyframe(
        fabricObjectClone.height,
        fabricObjectClone.top,
        fabricObjectClone.opacity,
        fabricObject,
        fabricObjectClone.scaleY
      );
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_BOTTOM:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_RIGHT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_TOP:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_IN_LEFT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_BOTTOM:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_RIGHT:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_TOP:
    case BLOCK_REVEAL_ANIMATIONS.BLOCK_REVEAL_OUT_LEFT:
      if (!extras.canvas) {
        console.error(`No canvas provided for ${type} Keyframes`);
        return [];
      }

      if (!extras.objects) {
        console.error(`No extra objects provided for ${type} Keyframes`);
        return [];
      }

      return getBlockRevealBaseKeyframe(
        fabricObject,
        duration,
        extras.canvas,
        extras.objects[0],
        type
      );
    case CUSTOM_ANIMATIONS.GREAT_THINKER:
    case CUSTOM_ANIMATIONS.SUNNY_MORNINGS:
    case CUSTOM_ANIMATIONS.MADE_WITH_LOVE:
    case CUSTOM_ANIMATIONS.RISING_STRONG_IN:
    case CUSTOM_ANIMATIONS.RISING_STRONG_OUT:
    case CUSTOM_ANIMATIONS.BEAUTIFUL_QUESTIONS:
    case CUSTOM_ANIMATIONS.DOMINO_DREAMS:
    case CUSTOM_ANIMATIONS.COFFEE_MORNINGS:
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_IN:
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_OUT:
    case CUSTOM_ANIMATIONS.FIND_YOUR_ELEMENT:
    case CUSTOM_ANIMATIONS.THURSDAY:
    case CUSTOM_ANIMATIONS.HELLO_GOODBYE:
      return getCharacterLevelAnimationKeyframes(
        type,
        fabricObject,
        fabricObjectClone,
        duration,
        extras
      );
  }
};

const getCharacterLevelAnimationKeyframes = (
  type: CUSTOM_ANIMATIONS,
  fabricObject: any,
  fabricObjectClone: any,
  duration: number,
  extras: AnimationConfigExtraParams
) => {
  if (!extras.objects) {
    console.error(`No extra objects for ${type} at getBaseKeyframe`);
    return [];
  }

  const intiialOpacity = getShowKeyframesInitialOpacity(
    fabricObjectClone.opacity,
    type
  );

  const hideKeyframe = getHideWithOpacityKeyframe(extras.objects[0]);
  const showLettersKeyframe = getShowGroupObjectsBaseKeyframe(
    fabricObject,
    fabricObject.getObjects().slice(1),
    intiialOpacity
  );

  const characterLevelBaseKeyframes = getCharacterLevelAnimationBaseKeyframe(
    type,
    fabricObject,
    fabricObjectClone,
    duration,
    extras
  );

  return hideKeyframe.concat(showLettersKeyframe, characterLevelBaseKeyframes);
};

const getShowKeyframesInitialOpacity = (opacity: number, type: ANIMATIONS) => {
  switch (type) {
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_OUT:
    case CUSTOM_ANIMATIONS.RISING_STRONG_OUT:
      return opacity;
    default:
      return 0;
  }
};

export const getCharacterLevelAnimationBaseKeyframe = (
  type: CUSTOM_ANIMATIONS,
  fabricObject: any,
  fabricObjectClone: any,
  duration: number,
  extras: AnimationConfigExtraParams
): KeyframeConfig[] => {
  switch (type) {
    case CUSTOM_ANIMATIONS.GREAT_THINKER:
      return getGreatThinkerBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2
      );
    case CUSTOM_ANIMATIONS.SUNNY_MORNINGS:
      return getSunnyMorningsBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.scaleX,
        fabricObjectClone.scaleY,
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2
      );
    case CUSTOM_ANIMATIONS.RISING_STRONG_IN:
      if (!extras.objects) {
        console.error(`No extra objects for ${type} at getBaseKeyframe`);
        return [];
      }

      return getRisingStrongInBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2,
        extras.objects[0].height
      );
    case CUSTOM_ANIMATIONS.RISING_STRONG_OUT:
      if (!extras.objects) {
        console.error(`No extra objects for ${type} at getBaseKeyframe`);
        return [];
      }

      return getRisingStrongOutBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2,
        extras.objects[0].height
      );
    case CUSTOM_ANIMATIONS.MADE_WITH_LOVE:
      if (!extras.objects) {
        console.error(`No extra objects for ${type} at getBaseKeyframe`);
        return [];
      }

      return getMadeWithLoveBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2,
        extras.objects[0].height
      );
    case CUSTOM_ANIMATIONS.BEAUTIFUL_QUESTIONS:
      if (!extras.objects) {
        console.error(`No extra objects for ${type} at getBaseKeyframe`);
        return [];
      }

      return getBeautifulQuestionsBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2,
        extras.objects[0].height
      );
    case CUSTOM_ANIMATIONS.DOMINO_DREAMS:
      if (!extras.objects) {
        console.error(`No extra objects for ${type} at getBaseKeyframe`);
        return [];
      }

      return getDominoDreamsBaseKeyframe(
        fabricObject.getObjects().slice(1),
        extras.delay ?? 0,
        duration / 2,
        extras.objects[0].angle
      );
    case CUSTOM_ANIMATIONS.COFFEE_MORNINGS:
      return getCoffeeMorningsBaseKeyframe(
        fabricObject.getObjects().slice(1),
        extras.delay ?? 0,
        duration / 2
      );
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_IN:
      return getANewProductionInBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2
      );
    case CUSTOM_ANIMATIONS.A_NEW_PRODUCTION_OUT:
      return getANewProductionOutBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2
      );
    case CUSTOM_ANIMATIONS.FIND_YOUR_ELEMENT:
      if (!extras.objects) {
        console.error(`No extra objects for ${type} at getBaseKeyframe`);
        return [];
      }

      const showLineKeyframe = getShowKeyframe(
        [extras.objects[1]],
        fabricObjectClone.opacity
      );

      const findYourElementBaseKeyframe = getFindYourElementBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2,
        extras.objects[1]
      );

      return showLineKeyframe.concat(findYourElementBaseKeyframe);
    case CUSTOM_ANIMATIONS.THURSDAY:
      if (!extras.objects) {
        console.error(`No extra objects for ${type} at getBaseKeyframe`);
        return [];
      }

      const showLinesKeyframe = getShowKeyframe(
        [extras.objects[1], extras.objects[2]],
        fabricObjectClone.opacity
      );

      const thursdayBaseKeyframe = getThursdayBaseKeyframe(
        fabricObject.getObjects().slice(1),
        fabricObjectClone.opacity,
        extras.delay ?? 0,
        duration / 2,
        extras.objects[1],
        extras.objects[2]
      );

      return showLinesKeyframe.concat(thursdayBaseKeyframe);
    case CUSTOM_ANIMATIONS.HELLO_GOODBYE:
      if (!extras.objects) {
        console.error(`No extra objects for ${type} at getBaseKeyframe`);
        return [];
      }

      const showLeftLineKeyframe = getShowKeyframe(
        [extras.objects[1]],
        fabricObjectClone.opacity
      );

      const helloGoodbyeBaseKeyframe = getHelloGoodbyeBaseKeyframe(
        fabricObject,
        fabricObjectClone.opacity,
        duration / 2,
        fabricObjectClone.width,
        extras.objects[1]
      );

      return showLeftLineKeyframe.concat(helloGoodbyeBaseKeyframe);
  }
};
